import { atom, useAtom, useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { useEffect } from 'react';
import { companyIdAtom } from 'views/state';

import { appliedIntegrationIdsAtom, bpaMetricsRefreshAtom } from '../state';
import { BPAMetricsData } from './types';

interface RequestEntry {
  key: string;
  data?: BPAMetricsData;
  error?: string;
}

// Retain one response for the current application session when a drill-down
// temporarily unmounts the dashboard. Jotai discards it with its provider.
const metricsRequestAtom = atom<RequestEntry | undefined>(undefined);

/** One application-session cache entry; visibility never becomes its identity. */
export const useMetricsData = (isOpen: boolean) => {
  const companyId = useAtomValue(companyIdAtom);
  const integrationIds = useAtomValue(appliedIntegrationIdsAtom);
  const refresh = useAtomValue(bpaMetricsRefreshAtom);
  const [entry, setEntry] = useAtom(metricsRequestAtom);
  const integrationKey = Array.from(new Set(integrationIds))
    .sort((a, b) => a - b)
    .join(',');
  const key = `${companyId}:${integrationKey}:${refresh}`;

  useEffect(() => {
    if (entry && entry.key !== key) {
      setEntry(undefined);
      return;
    }
    if (!isOpen || !companyId || companyId === -1 || entry) return;

    // Install the entry before requesting: collapse/reopen and re-renders reuse
    // an in-flight request as well as a completed response or an explicit error.
    const request: RequestEntry = { key };
    setEntry(request);
    const fetchMetrics = async () => {
      try {
        const response = await callApi('bpa/metrics/', 'GET', {
          company_id: companyId,
          integration_ids: integrationKey,
        });
        if (!response?.data || response.status === 499)
          throw new Error('Metrics request did not complete.');
        request.data = response.data as BPAMetricsData;
      } catch {
        request.error = 'Unable to load vulnerability metrics. Please retry.';
      }
      // A company/filter change or retry may have installed another request.
      setEntry((current) => (current === request ? { ...request } : current));
    };
    void fetchMetrics();
  }, [companyId, entry, integrationKey, isOpen, key, setEntry]);

  const current = entry?.key === key ? entry : undefined;
  return {
    data: current?.data,
    error: current?.error,
    loading:
      isOpen &&
      !current?.data &&
      !current?.error &&
      !!companyId &&
      companyId !== -1,
    retry: () => {
      setEntry((currentEntry) =>
        currentEntry?.key === key ? undefined : currentEntry,
      );
    },
  };
};
